import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Stack, Typography, TextField, Button, Box, IconButton, Menu, MenuItem, CircularProgress, Chip,
  Alert, Tooltip, LinearProgress, Skeleton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {
  SalonMember, TaskComment, taskApi, taskErrorMessage,
} from '../../services/taskService';
import { formatDateTime, formatFileSize, userLabel } from './taskShared';
import { withMemberName } from './TaskHistory';
import { SectionCard, EmptyState, StatusPill, PersonAvatar, useConfirm } from '../../components/ui';
import { tokens } from '../../theme';

interface Props {
  taskId: string;
  members: SalonMember[];
  currentUserId: string;
  canComment: boolean;
  canManage: boolean;
  onChanged: () => void;
  onNotify: (msg: string, severity: 'success' | 'error' | 'info') => void;
  onOpenAttachment: (attachmentId: string, isImage: boolean, name: string) => void;
}

const PAGE_SIZE = 30;

/** Na telefonie przyciski narzedzi zostaja same ikony, od tabletu z podpisem. */
const toolButtonSx = {
  minWidth: 0,
  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
} as const;

export const TaskComments: React.FC<Props> = ({
  taskId, members, currentUserId, canComment, canManage, onChanged, onNotify, onOpenAttachment,
}) => {
  const confirm = useConfirm();
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState<null | HTMLElement>(null);
  const [menu, setMenu] = useState<{ el: HTMLElement; comment: TaskComment } | null>(null);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const res = await taskApi.listComments(taskId, targetPage, PAGE_SIZE);
      setComments((prev) => (targetPage === 1 ? res.data : [...res.data, ...prev]));
      setTotal(res.pagination.total);
      setPage(res.pagination.page);
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się wczytać komentarzy.'));
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(1); }, [load]);

  const addMention = (member: SalonMember) => {
    const label = userLabel({ id: member.userId, ...member });
    setBody((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}@${label} `);
    setMentions((m) => Array.from(new Set([...m, member.userId])));
    setMentionAnchor(null);
  };

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    setError('');
    try {
      // Only ids whose name still appears in the text are sent as mentions.
      const activeMentions = mentions.filter((id) => {
        const m = members.find((x) => x.userId === id);
        return m ? text.includes(userLabel({ id: m.userId, ...m })) : false;
      });
      const comment = await taskApi.addComment(taskId, text, activeMentions);
      if (files.length > 0) {
        setUploadPercent(0);
        await taskApi.uploadAttachments(taskId, files, {
          commentId: comment.id,
          onProgress: setUploadPercent,
        });
      }
      setBody('');
      setMentions([]);
      setFiles([]);
      setUploadPercent(null);
      await load(1);
      onChanged();
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się dodać komentarza.'));
      setUploadPercent(null);
    } finally {
      setSending(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await taskApi.updateComment(editing.id, editing.body.trim());
      setEditing(null);
      load(1);
    } catch (err) {
      onNotify(taskErrorMessage(err, 'Nie udało się zapisać komentarza.'), 'error');
    }
  };

  const removeComment = async (comment: TaskComment) => {
    setMenu(null);
    const preview = comment.body.length > 140 ? `${comment.body.slice(0, 140)}…` : comment.body;
    const ok = await confirm({
      title: 'Usunąć komentarz?',
      details: (
        <>
          <Box component="span" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>{userLabel(withMemberName(comment.author, members))}</Box>
          <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{preview}</Box>
        </>
      ),
      description: 'Komentarz zniknie z zadania. Tej operacji nie można cofnąć.',
      confirmLabel: 'Usuń komentarz',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await taskApi.deleteComment(comment.id);
      load(1);
      onChanged();
      onNotify('Komentarz usunięty.', 'success');
    } catch (err) {
      onNotify(taskErrorMessage(err, 'Nie udało się usunąć komentarza.'), 'error');
    }
  };

  return (
    <SectionCard
      title={(
        <>
          Komentarze
          {total > 0 && (
            <Box component="span" sx={{ ml: 0.75, color: tokens.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {total}
            </Box>
          )}
        </>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {total > comments.length && (
        <Button variant="text" size="small" onClick={() => load(page + 1)} disabled={loading} sx={{ mb: 1.5, ml: -1.5 }}>
          Wczytaj starsze komentarze
        </Button>
      )}

      {loading && comments.length === 0 ? (
        <Stack gap={2} aria-busy="true" aria-label="Ładowanie komentarzy">
          {[0, 1].map((i) => (
            <Stack key={i} direction="row" gap={1.25}>
              <Skeleton variant="circular" width={36} height={36} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="30%" height={18} />
                <Skeleton variant="rounded" height={52} sx={{ mt: 0.75 }} />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : comments.length === 0 ? (
        <EmptyState
          compact
          icon={<ChatBubbleOutlineIcon />}
          title="Brak komentarzy"
          description={canComment
            ? 'Napisz pierwszy — zapytaj o szczegóły albo daj znać, jak idzie praca.'
            : 'Nikt jeszcze nie skomentował tego zadania.'}
        />
      ) : (
        <Stack gap={2.25}>
          {comments.map((comment) => {
            const author = withMemberName(comment.author, members);
            const mine = comment.authorId === currentUserId;
            return (
              <Stack key={comment.id} direction="row" gap={1.25} alignItems="flex-start">
                <PersonAvatar
                  firstName={author?.firstName}
                  lastName={author?.lastName}
                  fallback={author?.email}
                  seed={comment.authorId}
                  size={36}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="flex-start" gap={1}>
                    <Stack direction="row" alignItems="center" columnGap={1} rowGap={0.25} flexWrap="wrap" sx={{ minHeight: 30, flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, wordBreak: 'break-word' }}>
                        {userLabel(author)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {formatDateTime(comment.createdAt)}
                        {comment.editedAt ? ' · edytowany' : ''}
                      </Typography>
                      {comment.isRework && <StatusPill label="Zwrot do poprawy" tone="warning" size="small" />}
                    </Stack>
                    {(mine || canManage) && (
                      <IconButton
                        size="small"
                        aria-label="Opcje komentarza"
                        onClick={(e) => setMenu({ el: e.currentTarget, comment })}
                        sx={{ mr: -0.75, flexShrink: 0 }}
                      >
                        <MoreVertIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Stack>

                  {editing?.id === comment.id ? (
                    <Stack gap={1} sx={{ mt: 0.75 }}>
                      <TextField
                        fullWidth multiline minRows={2} value={editing.body}
                        onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                        inputProps={{ maxLength: 5000, 'aria-label': 'Treść komentarza' }}
                        autoFocus
                      />
                      <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button variant="text" size="small" onClick={() => setEditing(null)}>Anuluj</Button>
                        <Button variant="contained" size="small" onClick={saveEdit} disabled={!editing.body.trim()}>
                          Zapisz zmiany
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        mt: 0.5,
                        px: 1.75,
                        py: 1.25,
                        borderRadius: '4px 14px 14px 14px',
                        bgcolor: comment.isRework ? tokens.warningSoft : mine ? tokens.plumSoft : tokens.surfaceStrong,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: tokens.ink }}>
                        {comment.body}
                      </Typography>
                    </Box>
                  )}

                  {comment.attachments.length > 0 && (
                    <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                      {comment.attachments.map((a) => (
                        <Chip
                          key={a.id}
                          variant="outlined"
                          icon={<AttachFileIcon />}
                          label={`${a.originalName} · ${formatFileSize(a.sizeBytes)}`}
                          onClick={() => onOpenAttachment(a.id, a.isImage, a.originalName)}
                          sx={{ maxWidth: '100%' }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}

      {canComment && (
        <>
          <Box sx={{ mt: comments.length > 0 || loading ? 2.5 : 1, pt: 2, borderTop: `1px solid ${tokens.line}` }}>
            <TextField
              label="Nowy komentarz"
              multiline
              minRows={3}
              fullWidth
              placeholder="Pytanie, ustalenie albo informacja o postępie pracy…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
              inputProps={{ maxLength: 5000 }}
            />
            {files.length > 0 && (
              <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.25 }}>
                {files.map((f, i) => (
                  <Chip
                    key={`${f.name}-${i}`}
                    variant="outlined"
                    icon={<AttachFileIcon />}
                    label={`${f.name} · ${formatFileSize(f.size)}`}
                    onDelete={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    sx={{ maxWidth: '100%' }}
                  />
                ))}
              </Stack>
            )}
            {uploadPercent !== null && (
              <Box sx={{ mt: 1.25 }}>
                <LinearProgress variant="determinate" value={uploadPercent} />
                <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  Wysyłanie plików… {uploadPercent}%
                </Typography>
              </Box>
            )}
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 1.25 }}>
              <Tooltip title="Oznacz osobę — dostanie powiadomienie">
                <Button
                  variant="text"
                  startIcon={<AlternateEmailIcon />}
                  aria-label="Oznacz osobę w komentarzu"
                  onClick={(e) => setMentionAnchor(e.currentTarget)}
                  sx={toolButtonSx}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Oznacz osobę</Box>
                </Button>
              </Tooltip>
              <Tooltip title="Dołącz pliki do komentarza">
                <Button
                  variant="text"
                  startIcon={<AttachFileIcon />}
                  aria-label="Dodaj załącznik do komentarza"
                  onClick={() => fileInput.current?.click()}
                  sx={toolButtonSx}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Załącz plik</Box>
                </Button>
              </Tooltip>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  setFiles(Array.from(e.target.files || []).slice(0, 10));
                  if (fileInput.current) fileInput.current.value = '';
                }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, display: { xs: 'none', md: 'block' }, mr: 1, whiteSpace: 'nowrap' }}>
                Ctrl + Enter wysyła
              </Typography>
              <Button
                variant="contained"
                endIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                onClick={submit}
                disabled={sending || !body.trim()}
              >
                Wyślij
              </Button>
            </Stack>
          </Box>

          <Menu anchorEl={mentionAnchor} open={!!mentionAnchor} onClose={() => setMentionAnchor(null)}>
            {members.length === 0 && <MenuItem disabled>Brak osób do oznaczenia</MenuItem>}
            {members.map((m) => (
              <MenuItem key={m.userId} onClick={() => addMention(m)} sx={{ gap: 1.25 }}>
                <PersonAvatar firstName={m.firstName} lastName={m.lastName} fallback={m.email} seed={m.userId} size={28} />
                {userLabel({ id: m.userId, ...m })}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <Menu anchorEl={menu?.el} open={!!menu} onClose={() => setMenu(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {menu?.comment.authorId === currentUserId && (
          <MenuItem
            onClick={() => { setEditing({ id: menu!.comment.id, body: menu!.comment.body }); setMenu(null); }}
          >
            Edytuj
          </MenuItem>
        )}
        <MenuItem onClick={() => menu && removeComment(menu.comment)} sx={{ color: 'error.main' }}>
          Usuń
        </MenuItem>
      </Menu>
    </SectionCard>
  );
};

export default TaskComments;
